import { ArrowLeft, Download, Eye, FileText, Pencil, PenLine, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatHoursMinutes } from '@/lib/hours';
import { formatMoney } from '@/lib/money';
import * as api from '../api';
import { staffInvoiceStatusLabel, staffInvoiceStatusVariant } from '../constants';
import {
  useDeleteStaffInvoice,
  useStaffInvoice,
  useStaffInvoiceAction,
} from '../queries';

export function StaffInvoiceDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(false);
  const { data: inv, isPending, isError } = useStaffInvoice(publicId);
  const issue = useStaffInvoiceAction(api.issueStaffInvoice, 'Invoice issued');
  const genPdf = useStaffInvoiceAction(api.generateStaffInvoicePdf, 'PDF generated');
  const sign = useStaffInvoiceAction(api.signStaffInvoice, 'Invoice signed');
  const markPaid = useStaffInvoiceAction(api.markStaffInvoicePaid, 'Marked paid');
  const del = useDeleteStaffInvoice();

  // Auth-fetch the PDF (JWT/org headers) — a raw media `src` 401s / fails CORS.
  const fetchPdf = useCallback(() => api.getStaffInvoiceBlob(publicId), [publicId]);

  if (isPending) return <Skeleton className="h-64 w-full max-w-3xl" />;
  if (isError || !inv) return <p className="text-destructive">Could not load this invoice.</p>;

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/staff-invoices')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {inv.number || 'Staff invoice'}
          </h1>
          <Badge variant={staffInvoiceStatusVariant(inv.status)}>
            {staffInvoiceStatusLabel(inv.status)}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {inv.status === 'draft' ? (
            <Button variant="outline" onClick={() => navigate(`/finance/staff-invoices/${inv.public_id}/edit`)}>
              <Pencil /> Edit
            </Button>
          ) : null}
          {/* Generate only while draft — once issued the PDF is "out" (employee
              may hold it); the doc is then re-rendered automatically on sign. */}
          {inv.status === 'draft' ? (
            <Button variant="outline" onClick={() => genPdf.mutate(inv.public_id)} disabled={genPdf.isPending}>
              <FileText /> Generate PDF
            </Button>
          ) : null}
          {inv.document ? (
            <>
              <Button variant="outline" onClick={() => setPreview(true)}>
                <Eye /> View PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => api.downloadStaffInvoice(inv.public_id, api.staffInvoiceFileName(inv))}
              >
                <Download /> Download
              </Button>
            </>
          ) : null}
          {/* Issue only once a PDF has been generated — issuing notifies the
              employee and exposes the invoice, so there must be a document to
              read/sign first. */}
          {inv.status === 'draft' && inv.document ? (
            <Button onClick={() => issue.mutate(inv.public_id)} disabled={issue.isPending}>
              Issue
            </Button>
          ) : null}
          {/* Finance can sign on behalf of the employee (edge case). */}
          {inv.status === 'issued' ? (
            <Button onClick={() => sign.mutate(inv.public_id)} disabled={sign.isPending}>
              <PenLine /> {sign.isPending ? 'Signing…' : 'Sign'}
            </Button>
          ) : null}
          {inv.status === 'signed' ? (
            <Button onClick={() => markPaid.mutate(inv.public_id)} disabled={markPaid.isPending}>
              Mark paid
            </Button>
          ) : null}
          {/* A signed/paid invoice is a committed record — not deletable
              (also backend-guarded). */}
          {inv.status !== 'signed' && inv.status !== 'paid' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" aria-label="Delete invoice">
                  <Trash2 />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {inv.number ? `Invoice ${inv.number}` : 'This invoice'} will be permanently
                    deleted. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={del.isPending}
                    onClick={async () => {
                      await del.mutateAsync(inv.id);
                      navigate('/finance/staff-invoices');
                    }}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Employee">{inv.employee_name}</Field>
          <Field label="Period">
            {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
          </Field>
          <Field label="Hours">{formatHoursMinutes(inv.hours_worked)}</Field>
          <Field label="Hourly rate">
            {formatMoney(inv.hourly_rate, inv.currency) ?? '—'}
            {inv.hourly_rate ? <span className="text-muted-foreground"> / h</span> : null}
          </Field>
          <Field label="Total">{formatMoney(inv.total, inv.currency) ?? '—'}</Field>
          <Field label="Tax">
            {Number(inv.tax_rate) > 0
              ? `${inv.tax_rate}% · ${formatMoney(inv.tax_amount, inv.currency) ?? ''} (incl.)`
              : '—'}
          </Field>
          <Field label="Base (USD)">{formatMoney(inv.base_amount, 'USD') ?? '—'}</Field>
          <Field label="Issued">{fmtDate(inv.issue_date)}</Field>
          <Field label="Due">{fmtDate(inv.due_date)}</Field>
          <Field label="Signed">{inv.signed_at ? new Date(inv.signed_at).toLocaleString() : '—'}</Field>
        </CardContent>
      </Card>

      {inv.notes ? (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent className="whitespace-pre-line text-sm">{inv.notes}</CardContent>
        </Card>
      ) : null}

      <PdfPreviewDialog
        open={preview}
        onOpenChange={setPreview}
        title={`${inv.number || 'Staff invoice'} — PDF`}
        fetchBlob={inv.document ? fetchPdf : undefined}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
